import React, { useCallback } from "react";
import { get } from "lodash";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Button, Paragraph } from "@tvg/design-system";
import getOptedInPromos from "@tvg/utils/PromoUtils";
import { Promo, RaceProgram, RaceStatusEnum } from "@tvg/ts-types/Race";
import { useNavigateWithoutBet } from "@tvg/pp/src/hooks/useProgramPageNavigation";
import { getIsLogged } from "@urp/store-selectors";
import { Item, RaceNumberWrapper } from "./styled-components";
import { raceSelectorGtmEvent } from "../../../../utils/gtm/navigation";
import { getOptedInPromosStore } from "../../../../store/selectors";

interface RaceInterface {
  race: RaceProgram;
  isFirstOpenRace: boolean;
  isActive: boolean;
  isMobile?: boolean;
}

export const computeRaceStatus = (status: string): string =>
  status === RaceStatusEnum.RACE_OFF ? "OFF" : "";

export const ListItem = React.forwardRef<HTMLLIElement, RaceInterface>(
  ({ race, isActive = false, isFirstOpenRace, isMobile = false }, ref) => {
    const [searchParams] = useSearchParams();
    const selectedRaceNumber = searchParams.get("race");
    const raceStatus = get(race, "status.code", "");
    const { navigateWithoutBet } = useNavigateWithoutBet();

    const optedInPromos = useSelector(getOptedInPromosStore);
    const isLogged = useSelector(getIsLogged);

    const promo: Promo | null = getOptedInPromos(
      get(race, "promos", []),
      optedInPromos,
      isLogged
    );

    const handleRacePress = useCallback(() => {
      if (race.track && race.raceNumber) {
        navigateWithoutBet(
          race.track.trackCode,
          race.raceNumber,
          race.track.trackName
        );

        raceSelectorGtmEvent({
          raceNumber: race.raceNumber,
          destinationUrl: `${window.location.href}`,
          trackName: race.track.trackName,
          currentRaceNumber: selectedRaceNumber
        });
      }
    }, [race.track, race.raceNumber, isActive]);

    return (
      <Item
        isFirstOpenRace={isFirstOpenRace}
        key={race.id}
        ref={ref}
        isActive={isActive}
        hasPromo={!!promo}
        isOptIn={!!promo?.isOptedIn}
        data-qa-label={`race-list-item-${race.raceNumber}`}
        data-qa-active={isActive ? "active" : ""}
        borderWidth={isMobile ? 2 : 1}
      >
        <Button
          variant="secondary_dark"
          qaLabel={`race-button-${race.raceNumber}`}
          onPress={handleRacePress}
          isStretched
          borderRadius={0}
          mt={promo ? "-2px" : "0"}
        >
          <RaceNumberWrapper>
            <Paragraph
              qaLabel={`race-number-${race.raceNumber}`}
              color={
                raceStatus !== RaceStatusEnum.RACE_OFFICIAL
                  ? "white.900"
                  : "grey.600"
              }
              fontSize="s"
              fontFamily="medium"
            >
              {`R${race.raceNumber}`}
            </Paragraph>
            {computeRaceStatus(raceStatus) !== "" && (
              <Paragraph
                qaLabel="race-status"
                color="green.400"
                fontSize="xs"
                mx="space-4"
                mt={2}
              >
                {computeRaceStatus(raceStatus)}
              </Paragraph>
            )}
          </RaceNumberWrapper>
        </Button>
      </Item>
    );
  }
);

export default React.memo(ListItem);
